import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ListTodo } from 'lucide-react'
import { trpc } from '@/lib/trpc'
import {
  formatToolValue,
  getToolCallId,
  isToolError,
  normalizeTone,
  pickString,
  plural,
  readToolState,
  ToolDetailOverlayLink,
  ToolListPreview,
  ToolStatusLine,
  type ToolPartProps,
} from './_base'

const completedIcon = (
  <ListTodo className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
)

function getAssigneeName(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null
  const assignee = value as Record<string, unknown>
  return pickString(assignee.name, assignee.email, assignee.id)
}

function formatControlRefs(value: unknown): string | null {
  if (!Array.isArray(value)) return null
  const refs = value
    .map((control) =>
      typeof control === 'string'
        ? control
        : pickString(
            control?.displayCode,
            control?.id,
            control?.controlId,
            control?.title
          )
    )
    .filter(Boolean)
  return refs.length > 0 ? refs.join(', ') : null
}

export function TestsListToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const tests: Array<Record<string, unknown>> = Array.isArray(
    state.result?.tests
  )
    ? state.result.tests
    : []
  const total =
    typeof state.result?.total === 'number' ? state.result.total : tests.length
  const filterDetail = pickString(
    state.args?.controlRef,
    state.args?.frameworkControlRef,
    state.args?.status,
    state.args?.search,
    state.args?.checkId
  )
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading tests...'
        completedLabel={
          total !== null ? `${plural(total, 'test')} found` : 'Tests loaded'
        }
        loadingDetail={filterDetail}
        completedDetail={filterDetail}
        completedIcon={completedIcon}
      />
      {state.isComplete && !state.isError && !isToolError(state) ? (
        <ToolListPreview
          columns={['Test', 'Category', 'Status', 'Assignee', 'Controls']}
          emptyLabel='No tests found'
          items={tests.map((test) => ({
            id: pickString(test.id, test.taskCode),
            title: pickString(test.title) ?? 'Untitled test',
            subtitle: pickString(test.evidenceCategory, test.templateType),
            meta: [
              test.status,
              getAssigneeName(test.assignedTo),
              formatControlRefs(test.controlCodes ?? test.controlRefs),
            ],
            tone: normalizeTone(test.status),
          }))}
        />
      ) : null}
    </>
  )
}

export function TestsGetToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const test = state.result?.test ?? state.result
  const subtasks: Array<Record<string, unknown>> = Array.isArray(test?.subtasks)
    ? test.subtasks
    : []
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading test...'
        completedLabel='Test loaded'
        loadingDetail={pickString(state.args?.testId, state.args?.taskCode)}
        completedDetail={pickString(
          test?.taskCode,
          test?.title,
          state.args?.testId,
          state.args?.taskCode
        )}
        completedIcon={completedIcon}
      />
      {state.isComplete && !state.isError && !isToolError(state) && test ? (
        <>
          <ToolDetailOverlayLink
            overlayId={getToolCallId(
              part,
              `test-${pickString(test.id, test.taskCode, state.args?.testId) ?? 'detail'}`
            )}
            systemDetail={
              pickString(test.id, state.args?.testId)
                ? {
                    entityType: 'test',
                    entityId: pickString(test.id, state.args?.testId)!,
                    workspaceId: '',
                  }
                : null
            }
            payload={{
              entityType: 'test',
              toolName: 'testsGet',
              title: pickString(test.title) ?? 'Untitled test',
              subtitle: pickString(test.taskCode, test.id, state.args?.testId),
              description: pickString(test.description, test.instructions),
              fields: [
                {
                  label: 'Status',
                  value: formatToolValue(test.status),
                  tone: normalizeTone(test.status),
                },
                {
                  label: 'Evidence category',
                  value: formatToolValue(test.evidenceCategory),
                },
                {
                  label: 'Template type',
                  value: formatToolValue(test.templateType),
                },
                {
                  label: 'App type',
                  value: formatToolValue(test.appType),
                },
                {
                  label: 'Assigned to',
                  value: getAssigneeName(test.assignedTo),
                },
                { label: 'Due date', value: formatToolValue(test.dueDate) },
                {
                  label: 'Last tested',
                  value: formatToolValue(test.lastTestedAt),
                },
                {
                  label: 'Controls',
                  value: formatControlRefs(test.controlRefs),
                },
                {
                  label: 'Active submission',
                  value: formatToolValue(test.activeSubmission?.status),
                  tone: normalizeTone(test.activeSubmission?.status),
                },
              ],
              sections: [
                {
                  title: 'Instructions',
                  content: formatToolValue(test.instructions),
                },
              ],
            }}
          />
          {subtasks.length > 0 ? (
            <ToolListPreview
              emptyLabel='No child tests found'
              items={subtasks.map((subtask) => ({
                id: pickString(subtask.id, subtask.taskCode),
                title: pickString(subtask.title) ?? 'Untitled child test',
                subtitle: pickString(subtask.taskCode),
                meta: [subtask.status, getAssigneeName(subtask.assignedTo)],
                tone: normalizeTone(subtask.status),
              }))}
            />
          ) : null}
        </>
      ) : null}
    </>
  )
}

export function TestsListSubtasksToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const subtasks: Array<Record<string, unknown>> = Array.isArray(
    state.result?.subtasks
  )
    ? state.result.subtasks
    : []
  const total =
    typeof state.result?.total === 'number'
      ? state.result.total
      : subtasks.length
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading child tests...'
        completedLabel={
          total !== null
            ? `${plural(total, 'child test')} found`
            : 'Child tests loaded'
        }
        completedDetail={pickString(state.args?.parentId)}
        completedIcon={completedIcon}
      />
      {state.isComplete && !state.isError && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No child tests found'
          items={subtasks.map((subtask) => ({
            id: pickString(subtask.id, subtask.taskCode),
            title: pickString(subtask.title) ?? 'Untitled child test',
            subtitle: pickString(subtask.taskCode, subtask.subtaskSource),
            meta: [
              subtask.status,
              getAssigneeName(subtask.assignedUser) ??
                getAssigneeName(subtask.assignedTo),
              subtask.dueDate,
            ],
            tone: normalizeTone(subtask.status),
          }))}
        />
      ) : null}
    </>
  )
}

export function TestsAssignToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Assigning test...'
      completedLabel='Test assigned'
      completedDetail={pickString(state.args?.email, state.args?.testId)}
      completedIcon={completedIcon}
    />
  )
}

export function TestsUpdateStatusToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Updating test status...'
      completedLabel='Test status updated'
      completedDetail={pickString(state.args?.status, state.args?.testId)}
      completedIcon={completedIcon}
    />
  )
}

export function TestsCompleteToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Completing test...'
      completedLabel='Test completed'
      completedDetail={pickString(state.args?.testId)}
      completedIcon={completedIcon}
    />
  )
}

export function TestsSetDueDateToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Setting due date...'
      completedLabel='Due date set'
      completedDetail={pickString(state.args?.dueDate, state.args?.testId)}
      completedIcon={completedIcon}
    />
  )
}

export function TestsAssignSubtaskToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Assigning child test...'
      completedLabel='Child test assigned'
      completedDetail={pickString(
        state.args?.email,
        state.args?.childTestId,
        state.args?.subtaskId
      )}
      completedIcon={completedIcon}
    />
  )
}

export function TestsCreateToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const test = state.result?.test ?? state.result
  const createdId =
    pickString(test?.id, state.result?.testId, state.result?.id) ??
    pickString(state.args?.parentId)
  useInvalidateTestListQueries(
    createdId,
    state.isComplete,
    state.isError || isToolError(state)
  )
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Creating test...'
      completedLabel={
        state.args?.parentId ? 'Child test created' : 'Test created'
      }
      completedDetail={pickString(
        test?.taskCode,
        test?.title,
        state.result?.taskCode,
        state.result?.title,
        createdId
      )}
      completedIcon={completedIcon}
    />
  )
}

export function TestsBulkAssignToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const count =
    typeof state.result?.assignedCount === 'number'
      ? state.result.assignedCount
      : null
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Bulk assigning tests...'
      completedLabel={
        count !== null ? `Assigned ${plural(count, 'test')}` : 'Tests assigned'
      }
      completedDetail={pickString(state.args?.email)}
      completedIcon={completedIcon}
    />
  )
}

// Shared #446 invalidation set — anything that mutates an API test's
// run history or status (testsConvert, apiTestRunStart) needs to
// refresh the same query keys to keep the test detail pane in sync
// without a manual reload.
function useInvalidateApiTestQueries(
  testId: string | null,
  isComplete: boolean,
  isError: boolean
) {
  const queryClient = useQueryClient()
  useEffect(() => {
    if (!isComplete || isError || !testId) return
    queryClient.invalidateQueries({
      queryKey: trpc.guidedTemplate.getStatusById.queryKey({ id: testId }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.guidedTemplate.listApiTestRuns.queryKey({
        statusId: testId,
      }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.guidedTemplate.getLatestApiTestRun.queryKey({
        statusId: testId,
      }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.compliance.getControlsTree.queryKey(),
    })
  }, [isComplete, isError, testId, queryClient])
}

function useInvalidateTestListQueries(
  testId: string | null,
  isComplete: boolean,
  isError: boolean
) {
  const queryClient = useQueryClient()
  useEffect(() => {
    if (!isComplete || isError) return
    queryClient.invalidateQueries({
      queryKey: trpc.compliance.getControlsTree.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.guidedTemplate.getMyTasks.queryKey(),
    })
    if (testId) {
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getStatusById.queryKey({ id: testId }),
      })
    }
  }, [isComplete, isError, testId, queryClient])
}

export function TestsConvertToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  // #446: convert rewrites templateId / executionConfig / status /
  // activeSubmissionId on the CES. Without invalidation the test detail
  // pane keeps showing the OLD check, run history, and submission badge.
  const testId =
    pickString(state.result?.testId) ?? pickString(state.args?.testId)
  useInvalidateApiTestQueries(testId, state.isComplete, state.isError)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Converting test...'
      completedLabel='Test converted'
      completedDetail={pickString(state.args?.testId)}
      completedIcon={completedIcon}
    />
  )
}

export function SearchNativeChecksToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const checks: Array<Record<string, unknown>> = Array.isArray(
    state.result?.checks
  )
    ? state.result.checks
    : []
  const total =
    typeof state.result?.total === 'number' ? state.result.total : checks.length
  const recommendedCheckId = pickString(state.result?.recommendedCheckId)
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Searching automated checks...'
        completedLabel={
          total === 0
            ? 'No automated checks matched'
            : `${plural(total, 'automated check')} found`
        }
        loadingDetail={pickString(state.args?.testId, state.args?.provider)}
        completedDetail={
          recommendedCheckId
            ? `recommended: ${recommendedCheckId}`
            : pickString(state.args?.provider)
        }
        completedIcon={completedIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No automated checks matched this test'
          items={checks.map((check) => {
            const checkId = pickString(check.checkId)
            const connected = check.connected === true
            const isRecommended =
              !!recommendedCheckId && checkId === recommendedCheckId
            return {
              id: checkId,
              title: pickString(check.name) ?? checkId ?? 'Check',
              subtitle: checkId,
              meta: [
                isRecommended
                  ? 'recommended'
                  : connected
                    ? 'connected'
                    : 'not connected',
                pickString(check.provider),
                pickString(check.readiness),
              ],
              tone: isRecommended ? 'success' : connected ? 'low' : 'default',
            }
          })}
        />
      ) : null}
    </>
  )
}

export function ApiCheckBindToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  // Binding rewrites executionConfig (checkId/provider) on the CES, so the
  // test detail pane's "automated check" + run history must refresh.
  const testId =
    pickString(state.result?.testId) ?? pickString(state.args?.testId)
  useInvalidateApiTestQueries(testId, state.isComplete, state.isError)
  const checkId = pickString(state.result?.checkId, state.args?.checkId)
  const provider = pickString(state.result?.provider)
  const detail =
    checkId && provider
      ? `${checkId} · ${provider}`
      : (checkId ?? pickString(state.args?.testId))
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Binding automated check...'
      completedLabel='Automated check bound'
      completedDetail={detail}
      completedIcon={completedIcon}
    />
  )
}

export function ApiTestRunStartToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  // #446: every successful apiTestRunStart writes a new ApiTestRun row
  // and flips CES.status to IN_PROGRESS. The "View all runs" panel and
  // latest-run badge stay stale until these queries invalidate.
  const testId =
    pickString(state.result?.testId) ?? pickString(state.args?.testId)
  useInvalidateApiTestQueries(testId, state.isComplete, state.isError)
  const runId = pickString(state.result?.runId)
  const resultStatus = pickString(state.result?.resultStatus)
  const detail =
    runId && resultStatus
      ? `${resultStatus} · ${runId}`
      : (runId ?? pickString(state.args?.testId))
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Running automated check...'
      completedLabel='Automated check finished'
      completedDetail={detail}
      completedIcon={completedIcon}
    />
  )
}

function formatRunTimestamp(value: unknown): string | null {
  if (!value || (typeof value !== 'string' && !(value instanceof Date))) {
    return null
  }
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function ApiTestRunListToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const runs: Array<Record<string, unknown>> = Array.isArray(state.result?.runs)
    ? state.result.runs
    : []
  const count =
    typeof state.result?.count === 'number' ? state.result.count : runs.length
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading run history...'
        completedLabel={
          count === 0
            ? 'No runs yet'
            : `${plural(count, 'run')} loaded`
        }
        completedDetail={pickString(state.args?.testId)}
        completedIcon={completedIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No runs yet'
          items={runs.map((run) => ({
            id: pickString(run.id),
            title:
              pickString(run.apiCheckKey) ??
              (pickString(run.id) || 'Run'),
            subtitle: formatRunTimestamp(run.finishedAt ?? run.startedAt),
            meta: [run.resultStatus, run.phase, pickString(run.id)],
            tone: normalizeTone(run.resultStatus),
          }))}
        />
      ) : null}
    </>
  )
}

export function ApiTestRunGetToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const run = (state.result?.run ?? state.result) as
    | Record<string, unknown>
    | undefined
  const resultStatus = pickString(run?.resultStatus)
  const phase = pickString(run?.phase)
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading run details...'
        completedLabel='Run details loaded'
        completedDetail={
          resultStatus && phase
            ? `${resultStatus} · ${phase}`
            : (resultStatus ?? phase ?? pickString(state.args?.runId))
        }
        completedIcon={completedIcon}
      />
      {state.isComplete && !isToolError(state) && run ? (
        <ToolDetailOverlayLink
          overlayId={getToolCallId(
            part,
            `apirun-${pickString(run.id, state.args?.runId) ?? 'detail'}`,
          )}
          payload={{
            entityType: 'api_test_run',
            toolName: 'apiTestRunGet',
            title:
              pickString(run.apiCheckKey) ??
              (pickString(run.id) || 'API test run'),
            subtitle: pickString(run.id, state.args?.runId),
            fields: [
              { label: 'Result', value: formatToolValue(run.resultStatus) },
              { label: 'Phase', value: formatToolValue(run.phase) },
              { label: 'Provider', value: formatToolValue(run.provider) },
              { label: 'Check', value: formatToolValue(run.apiCheckKey) },
              { label: 'Started', value: formatRunTimestamp(run.startedAt) },
              { label: 'Finished', value: formatRunTimestamp(run.finishedAt) },
              {
                label: 'Duration',
                value:
                  typeof run.durationMs === 'number'
                    ? `${Math.round((run.durationMs as number) / 1000)}s`
                    : null,
              },
            ],
          }}
        />
      ) : null}
    </>
  )
}
